import { getUserManager } from '@/lib/auth/keycloak';
import { config } from '@/lib/config';
import { useAuthStore } from '@/lib/store/auth';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
/**
 * Cross-tenant Partnership Controller client.
 *
 * Mirrors the shipped OpenAPI surface (partnerships.sandbox.stage.flysafe.live):
 *   GET    /partnerships/approved-partners
 *   POST   /partnerships/approved-partners            { partner_tenant }
 *   DELETE /partnerships/approved-partners/{tenant}
 *   POST   /partnerships/requests                     { requesting_tenant }
 *   GET    /partnerships/requests?status=
 *   PATCH  /partnerships/requests/{id}/approve        { notes? }
 *   PATCH  /partnerships/requests/{id}/deny           { notes? }
 *
 * NOTE: the live spec declares no response body schema and `/requests` was
 * empty when probed, so `PartnershipRequest` below is provisional — confirm
 * the request-object fields with BE (or by creating one test request).
 * `approved-partners` shape IS confirmed: `{ partner_tenant, added_at }`.
 *
 * In mock mode MSW serves these from in-memory state (see api/mock/handlers).
 */
import ky from 'ky';

export interface ApprovedPartner {
  partner_tenant: string;
  added_at: string;
}

export type RequestStatus = 'pending' | 'approved' | 'denied';

export interface PartnershipRequest {
  id: number;
  requesting_tenant: string;
  status: RequestStatus;
  created_at: string;
  decided_at: string | null;
  notes: string | null;
}

const client = ky.create({
  // Empty base → same-origin '/' so the request goes through the dev proxy /
  // worker `/partnerships` route (mirrors http.ts `config.api.base || '/'`).
  prefixUrl: config.partnerships.base || '/',
  timeout: 10_000,
  retry: { limit: 2, methods: ['get'] },
  hooks: {
    beforeRequest: [
      async (req) => {
        const user = useAuthStore.getState().user;
        if (user) req.headers.set('X-Tenant-Id', user.tenantId);

        // 86e232ykg landed, so a real access token now exists whenever the
        // session came from Keycloak (getUserManager() is null for a mock
        // role-picker session — no-op then, same as before).
        //
        // UNCONFIRMED: BE never said the Partnership Controller actually
        // accepts flysafe-frontend-<tenant> tokens, or what scope/audience
        // it expects — that sentence in the old comment here was never
        // answered, only the general-login side of 86e232ykg was. If this
        // still 401s after a real login (not just a missing token), that's
        // new information for BE: the token is being sent and rejected,
        // not simply absent.
        const oidcUser = await getUserManager()?.getUser();
        if (oidcUser?.access_token) {
          req.headers.set('Authorization', `Bearer ${oidcUser.access_token}`);
        }
      },
    ],
  },
});

const KEY = {
  approved: ['partnerships', 'approved-partners'] as const,
  requests: ['partnerships', 'requests'] as const,
};

// The Partnership Controller is a separate origin; if it's unreachable
// (CORS not yet enabled in live, service down) we don't want a retry storm —
// fail fast and let the UI render its empty/error state. Pass `enabled` so
// callers only fetch when their surface is actually visible.
export function useApprovedPartners(opts?: { enabled?: boolean }) {
  return useQuery({
    queryKey: KEY.approved,
    queryFn: () => client.get('partnerships/approved-partners').json<ApprovedPartner[]>(),
    staleTime: 30_000,
    retry: false,
    enabled: opts?.enabled ?? true,
  });
}

export function usePartnershipRequests(opts?: { enabled?: boolean }) {
  return useQuery({
    queryKey: KEY.requests,
    queryFn: () => client.get('partnerships/requests').json<PartnershipRequest[]>(),
    staleTime: 15_000,
    retry: false,
    enabled: opts?.enabled ?? true,
  });
}

export function useAddApprovedPartner() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (partner_tenant: string) =>
      client
        .post('partnerships/approved-partners', { json: { partner_tenant } })
        .json<ApprovedPartner>(),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY.approved }),
  });
}

export function useRemoveApprovedPartner() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (partner_tenant: string) =>
      client
        .delete(`partnerships/approved-partners/${encodeURIComponent(partner_tenant)}`)
        .then(() => partner_tenant),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY.approved }),
  });
}

export function useSubmitRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (requesting_tenant: string) =>
      client
        .post('partnerships/requests', { json: { requesting_tenant } })
        .json<PartnershipRequest>(),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY.requests }),
  });
}

export function useDecideRequest(kind: 'approve' | 'deny') {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, notes }: { id: number; notes?: string }) =>
      client
        .patch(`partnerships/requests/${id}/${kind}`, { json: { notes: notes ?? null } })
        .json<PartnershipRequest>(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY.requests });
      qc.invalidateQueries({ queryKey: KEY.approved });
    },
  });
}
